import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEOHead from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { 
  BookOpen, 
  ArrowLeft, 
  Download, 
  Crown, 
  Mail, 
  Phone,
  ShoppingCart,
  CheckCircle2,
  Lock,
  Loader2,
  Receipt,
  FileText,
  Key,
  Copy
} from 'lucide-react';
import logo from '@/assets/logo-small.webp';

interface NoteFile {
  name: string;
  url: string;
}

const Dashboard: React.FC = () => {
  const { user, profile, hasPaymentAccess, isLoading, signOut } = useSupabaseAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [notes, setNotes] = useState<NoteFile[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);
  const [generatedCredentials, setGeneratedCredentials] = useState<{ email: string; password: string } | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<{ [key: string]: number }>({});
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);

  // Load generated credentials from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('generatedCredentials');
    if (stored) {
      try {
        setGeneratedCredentials(JSON.parse(stored));
      } catch (e) {
        // Ignore parse errors
      }
    }
  }, []);

  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/auth');
    }
  }, [user, isLoading, navigate]);

  useEffect(() => {
    if (hasPaymentAccess && user) {
      fetchNotes();
    }
  }, [hasPaymentAccess, user]);

  const fetchNotes = async () => {
    setLoadingNotes(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Session expired",
          description: "Please login again",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke('get-secure-notes-url', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      
      if (error || !data?.success) {
        throw new Error(data?.error || 'Failed to get notes');
      }
      
      setNotes(data.notes || []);
    } catch (error) {
      console.error('Fetch notes error:', error);
      toast({
        title: "Error",
        description: "Unable to load notes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const handleDownload = async (note: NoteFile) => {
    // Prevent multiple downloads of the same file
    if (downloadingFile === note.name) return;
    
    setDownloadingFile(note.name);
    setDownloadProgress(prev => ({ ...prev, [note.name]: 0 }));

    try {
      const response = await fetch(note.url);
      
      if (!response.ok) {
        throw new Error('Failed to fetch file');
      }

      const contentLength = response.headers.get('content-length');
      const total = contentLength ? parseInt(contentLength, 10) : 0;
      
      if (!response.body) {
        throw new Error('No response body');
      }

      const reader = response.body.getReader();
      const chunks: Uint8Array[] = [];
      let received = 0;

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        chunks.push(value);
        received += value.length;
        
        // Update progress
        if (total > 0) {
          const progress = Math.round((received / total) * 100);
          setDownloadProgress(prev => ({ ...prev, [note.name]: progress }));
        } else {
          // If no content-length, show indeterminate progress
          setDownloadProgress(prev => ({ ...prev, [note.name]: -1 }));
        }
      }

      // Combine chunks into a single blob
      const blob = new Blob(chunks as BlobPart[], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create and click download link
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = note.name;
      link.style.display = 'none';
      document.body.appendChild(link);
      
      setTimeout(() => {
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 1000);
      }, 100);

      toast({
        title: "Download Complete",
        description: `"${formatChapterName(note.name)}" saved to Downloads.`,
        duration: 4000,
      });
    } catch (error) {
      console.error('Download error:', error);
      window.open(note.url, '_blank');
      toast({
        title: "Opening PDF",
        description: "The PDF opened in a new tab. Use 'Save As' to download.",
        duration: 5000,
      });
    } finally {
      setDownloadingFile(null);
      setDownloadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[note.name];
        return newProgress;
      });
    }
  };

  const formatChapterName = (filename: string) => {
    return filename
      .replace(/\.pdf$/i, '')
      .replace(/\s*\(\d+\)\s*/g, '')
      .trim();
  };

  const handleDownloadReceipt = async () => {
    setDownloadingReceipt(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Session expired",
          description: "Please login again",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke('generate-receipt', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error || !data?.success) {
        throw new Error(data?.error || 'Failed to generate receipt');
      }

      // Create a Blob from the HTML content
      const blob = new Blob([data.receipt], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      
      // Open in new window for printing/saving
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          // Auto-trigger print dialog after a short delay
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
      }

      toast({
        title: "Receipt Generated",
        description: "Your payment receipt is ready. Use Ctrl+P to save as PDF.",
      });

    } catch (error) {
      console.error('Receipt error:', error);
      toast({
        title: "Error",
        description: "Unable to generate receipt. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDownloadingReceipt(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-subtle">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || !profile) return null;

  return (
    <>
      <SEOHead 
        title="Dashboard - TOPPERS BUDDY | Your Class 10th Science Notes"
        description="Access your purchased class 10th science short notes. Download toppers notes for Physics, Chemistry, and Biology."
        noIndex={true}
      />

      <div className="min-h-screen bg-gradient-subtle">
        {/* Header */}
        <header className="sticky top-0 z-50 border-b border-border/40 bg-background/95 backdrop-blur">
          <div className="container flex h-16 items-center justify-between px-4 md:px-6">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm"
                className="gap-2"
                onClick={() => navigate('/')}
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            </div>

            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <img 
                src={logo} 
                alt="Toppers Buddy Logo" 
                className="h-12 w-auto object-contain"
                width={88}
                height={48}
                loading="eager"
                decoding="async"
              />
              <span className="text-xl font-bold hidden sm:block">
                <span className="text-gradient">TOPPERS</span>
                <span className="text-foreground"> BUDDY</span>
              </span>
            </div>

            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleLogout}
              className="text-muted-foreground"
            >
              Logout
            </Button>
          </div>
        </header>

        {/* Main Content */}
        <main className="container px-4 py-8 md:px-6 md:py-12">
          <div className="mx-auto max-w-4xl">
            {/* Welcome Section */}
            <div className="mb-8 rounded-2xl border border-border/50 bg-card p-6 shadow-card md:p-8">
              <div className="flex flex-col items-center gap-4 md:flex-row md:gap-6">
                {/* Avatar */}
                <div className="relative">
                  <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-primary to-blue-600 text-2xl font-bold text-primary-foreground">
                    {profile.full_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  {hasPaymentAccess && (
                    <div className="absolute -right-1 -top-1 flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-gold to-amber-500">
                      <Crown className="h-4 w-4 text-gold-foreground" />
                    </div>
                  )}
                </div>

                {/* User Info */}
                <div className="flex-1 text-center md:text-left">
                  <div className="flex flex-col items-center gap-2 md:flex-row md:gap-3">
                    <h1 className="text-2xl font-bold text-foreground">
                      Welcome, {profile.full_name || 'User'}!
                    </h1>
                    {hasPaymentAccess && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-gold to-amber-500 px-3 py-1 text-xs font-semibold text-gold-foreground">
                        <Crown className="h-3 w-3" />
                        VIP MEMBER
                      </span>
                    )}
                  </div>
                  <div className="mt-3 flex flex-col gap-2 text-sm text-muted-foreground md:flex-row md:gap-4">
                    <span className="flex items-center justify-center gap-2 md:justify-start">
                      <Mail className="h-4 w-4" />
                      {profile.email}
                    </span>
                    {profile.mobile && (
                      <span className="flex items-center justify-center gap-2 md:justify-start">
                        <Phone className="h-4 w-4" />
                        {profile.mobile}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Login Credentials Section - show if password exists in DB or localStorage */}
              {(profile.auto_password || generatedCredentials?.password) && (
                <div className="mt-4 rounded-xl border border-primary/30 bg-primary/5 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Key className="h-5 w-5 text-primary" />
                    <span className="font-semibold text-foreground">Your Login Credentials</span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">
                    Save these credentials to login again in the future:
                  </p>
                  <div className="grid gap-2">
                    <div className="flex items-center justify-between rounded-lg bg-background p-3 border border-border">
                      <div>
                        <p className="text-xs text-muted-foreground">Email ID</p>
                        <p className="font-mono text-sm text-foreground">{profile.email}</p>
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(profile.email);
                          toast({ title: "Copied!", description: "Email copied to clipboard" });
                        }}
                        className="p-2 hover:bg-accent rounded-md"
                      >
                        <Copy className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between rounded-lg bg-background p-3 border border-border">
                      <div>
                        <p className="text-xs text-muted-foreground">Password</p>
                        <p className="font-mono text-sm text-foreground">{profile.auto_password || generatedCredentials?.password}</p>
                      </div>
                      <button
                        onClick={() => {
                          const pwd = profile.auto_password || generatedCredentials?.password || '';
                          navigator.clipboard.writeText(pwd);
                          toast({ title: "Copied!", description: "Password copied to clipboard" });
                        }}
                        className="p-2 hover:bg-accent rounded-md"
                      >
                        <Copy className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Notes Access Section */}
            {hasPaymentAccess ? (
              <div className="rounded-2xl border-2 border-success/30 bg-success/5 p-6 md:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-success/20">
                    <CheckCircle2 className="h-6 w-6 text-success" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-foreground">
                      PAID NOTES (VIP ACCESS)
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      You have full access to all {notes.length} chapters
                    </p>
                  </div>
                </div>

                {/* Notes Grid */}
                {loadingNotes ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary mr-3" />
                    <span className="text-muted-foreground">Loading your notes...</span>
                  </div>
                ) : notes.length > 0 ? (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {notes.map((note, index) => (
                      <div
                        key={index}
                        className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl border border-border/50 bg-card hover:bg-accent/50 hover:border-primary/50 transition-all shadow-sm"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                            <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground truncate text-sm sm:text-base">
                              {formatChapterName(note.name)}
                            </p>
                            <p className="text-xs text-muted-foreground">PDF Document</p>
                          </div>
                        </div>
                        <div className="w-full sm:w-auto">
                          {downloadingFile === note.name ? (
                            <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-primary/30 bg-primary/5">
                              <Loader2 className="h-4 w-4 animate-spin text-primary" />
                              <div className="flex flex-col min-w-[80px]">
                                <span className="text-xs text-primary font-medium">
                                  {downloadProgress[note.name] === -1 
                                    ? 'Downloading...' 
                                    : `${downloadProgress[note.name] || 0}%`}
                                </span>
                                {downloadProgress[note.name] !== -1 && (
                                  <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden mt-1">
                                    <div 
                                      className="h-full bg-primary transition-all duration-300 ease-out rounded-full"
                                      style={{ width: `${downloadProgress[note.name] || 0}%` }}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full sm:w-auto gap-1.5"
                              onClick={() => handleDownload(note)}
                            >
                              <Download className="h-4 w-4" />
                              Download
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 rounded-xl border border-border/50 bg-card">
                    <BookOpen className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-muted-foreground">Notes are being prepared. Please check back later.</p>
                    <Button variant="outline" className="mt-4" onClick={fetchNotes}>
                      Refresh
                    </Button>
                  </div>
                )}

                <div className="mt-6 flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 rounded-xl bg-gradient-to-r from-gold/10 to-amber-500/10 p-4">
                    <div className="flex items-center gap-3">
                      <Crown className="h-5 w-5 text-gold" />
                      <div>
                        <p className="font-medium text-foreground">VIP Benefits Active</p>
                        <p className="text-sm text-muted-foreground">
                          Lifetime access to notes • Priority support • Future updates included
                        </p>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="gap-2 shrink-0"
                    onClick={handleDownloadReceipt}
                    disabled={downloadingReceipt}
                  >
                    {downloadingReceipt ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Receipt className="h-4 w-4" />
                    )}
                    Download Receipt
                  </Button>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-border/50 bg-card p-6 text-center md:p-8">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                  <Lock className="h-8 w-8 text-muted-foreground" />
                </div>
                <h2 className="text-xl font-bold text-foreground mb-2">
                  No Notes Purchased Yet
                </h2>
                <p className="text-muted-foreground mb-6">
                  Purchase our premium short notes to unlock VIP access and start your preparation journey.
                </p>
                <Button 
                  variant="hero" 
                  size="lg" 
                  className="gap-2"
                  onClick={() => navigate('/')}
                >
                  <ShoppingCart className="h-5 w-5" />
                  Buy Notes Now - ₹149
                </Button>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default Dashboard;
